import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, Trash2, Pencil, Upload } from "lucide-react";

export default function Products() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", description: "", technical_specs: "", packaging_info: "", moq: "", certifications: "", images: [], is_public: true });
  const [uploading, setUploading] = useState(false);

  const load = async () => { const { data } = await api.get("/products"); setList(data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", category: "", description: "", technical_specs: "", packaging_info: "", moq: "", certifications: "", images: [], is_public: true }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, certifications: (p.certifications || []).join(", ") }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      certifications: typeof form.certifications === "string" ? form.certifications.split(",").map((s) => s.trim()).filter(Boolean) : form.certifications,
    };
    if (editing) await api.patch(`/products/${editing.id}`, payload);
    else await api.post("/products", payload);
    setOpen(false); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/products/${id}`); load();
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.products")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Products</h1>
        </div>
        <button onClick={openCreate} data-testid="product-create-button" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
          <Plus className="w-4 h-4" /> {t("common.add")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
        {list.length === 0 ? (
          <div className="col-span-full text-center text-slate-400 py-16 bg-white border border-slate-200 rounded-lg">{t("common.no_data")}</div>
        ) : list.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-all">
            <div className="aspect-[4/3] bg-slate-100 relative">
              {p.images?.[0] ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">No image</div>
              )}
            </div>
            <div className="p-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{p.category || "—"}</div>
              <h3 className="font-heading font-semibold text-slate-900 mt-1">{p.name}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.description || ""}</p>
              <div className="mt-3 text-xs text-slate-500">MOQ: {p.moq || "—"}</div>
              <div className="mt-4 flex justify-end gap-1">
                <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-[#0052CC]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full border border-slate-200 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{editing ? t("common.edit") : t("common.add")} Product</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-form">
              <Inp label="Name" testid="product-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Inp label="Category" testid="product-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
              <Inp label="MOQ" testid="product-moq" value={form.moq} onChange={(v) => setForm({ ...form, moq: v })} />
              <Inp label="Certifications (comma-separated)" testid="product-certs" value={form.certifications || ""} onChange={(v) => setForm({ ...form, certifications: v })} />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Description</label>
                <textarea data-testid="product-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm min-h-[60px]" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Technical specs</label>
                <textarea data-testid="product-specs" value={form.technical_specs || ""} onChange={(e) => setForm({ ...form, technical_specs: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm min-h-[60px]" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Packaging info</label>
                <textarea data-testid="product-packaging" value={form.packaging_info || ""} onChange={(e) => setForm({ ...form, packaging_info: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm min-h-[60px]" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Images</label>
                <div className="mt-1.5 flex gap-2 items-center flex-wrap">
                  {(form.images || []).map((u, i) => (
                    <div key={i} className="relative w-20 h-20 border border-slate-200 rounded overflow-hidden">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 text-xs">×</button>
                    </div>
                  ))}
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-[#0052CC]">
                    <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                    <input data-testid="product-image-upload" type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                  </label>
                </div>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input id="is_public" type="checkbox" checked={!!form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
                <label htmlFor="is_public" className="text-sm text-slate-700">Show on public profile</label>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{t("common.cancel")}</button>
                <button type="submit" data-testid="product-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}
